
import React, { useState, useEffect } from "react";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { keyframes } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { usePermissions } from "context"; // [NEW]
import UpgradePopup from "layouts/components/Upgrade"; // [NEW]

// 🎞️ Bounce animation (Centered)
const bounceFromBottom = keyframes`
  0% { transform: translateX(-50%) translateY(150%); opacity: 0; }
  40% { transform: translateX(-50%) translateY(-20px); opacity: 1; }
  65% { transform: translateX(-50%) translateY(8px); }
  85% { transform: translateX(-50%) translateY(-4px); }
  100% { transform: translateX(-50%) translateY(0); }
`;

function SocialPopup({
    selectedItems = [],
    onClearConfirm,
    onContinue,
    onBack,
    actionLabel = "Continue",
    entityType = "profile",
}) {
    const hasItems = selectedItems.length > 0;
    const theme = useTheme();
    const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

    // [NEW] RBAC State
    const [showRBAC, setShowRBAC] = useState(false);
    const { hasPermission, requiredRoles } = usePermissions();

    // ✅ Trigger animation once per show
    useEffect(() => {
        if (hasItems && !hasAnimatedIn) setHasAnimatedIn(true);
        else if (!hasItems) setHasAnimatedIn(false);
    }, [hasItems, hasAnimatedIn]);

    const handleContinue = () => {
        if (!hasPermission("reports:manage")) {
            setShowRBAC(true);
            return;
        }
        onContinue();
    };

    const getLabel = () => {
        let noun = "Profile";
        if (entityType === "post") noun = "Post";
        else if (entityType === "video") noun = "Video";
        else if (entityType === "snapshot") noun = "Snapshot";
        else if (entityType === "category") noun = "Category";

        const isPlural = selectedItems.length !== 1;
        let labelNoun = noun;
        if (isPlural) {
            if (entityType === "category") labelNoun = "Categories";
            else labelNoun = `${noun}s`;
        }

        return `${selectedItems.length} ${labelNoun} Selected`;
    };

    return (
        <>
            <Slide direction="up" in={hasItems} mountOnEnter unmountOnExit>
                <MDBox
                    sx={{
                        // Centering and Width Adjustments
                        position: "fixed",
                        bottom: 15,
                        left: "50%", // Center horizontal
                        transform: "translateX(-50%)", // Center horizontal
                        width: "95%", // Responsive width
                        maxWidth: "800px", // Maximum width constraint for larger screens

                        // Design/Height Adjustments
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 24px",
                        borderRadius: "12px",

                        zIndex: 1300,
                        border: "2px solid #00A1A1",
                        backgroundColor: theme.functions.rgba("#000", 0.6), // Dark translucent
                        backdropFilter: `saturate(200%) blur(30px)`,
                        boxShadow: "0 -3px 12px rgba(0,0,0,0.15)",
                        // Maintain centered state after animation
                        animation: hasAnimatedIn
                            ? `${bounceFromBottom} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
                            : "none",
                    }}
                >
                    {/* Left label */}
                    <Typography variant="h6" color="#fff" sx={{ fontWeight: 600 }}>
                        {getLabel()}
                    </Typography>

                    {/* Right Buttons */}
                    <MDBox
                        display="flex"
                        gap={2}
                        sx={{
                            flexWrap: { xs: "wrap", sm: "nowrap" },
                            justifyContent: 'center',
                        }}
                    >
                        {/* Continue / Primary Action */}
                        <MDButton
                            variant="contained"
                            color="accent"
                            onClick={handleContinue}
                            startIcon={<PlayArrowOutlinedIcon />}
                            sx={{
                                whiteSpace: "nowrap",
                                minWidth: { xs: "100%", sm: "180px" },
                                fontWeight: 500,
                            }}
                        >
                            {actionLabel}
                        </MDButton>

                        {/* Clear Selection */}
                        {onClearConfirm && (
                            <MDButton
                                variant="contained"
                                color="error"
                                onClick={onClearConfirm}
                                startIcon={<ClearOutlinedIcon />}
                                sx={{
                                    whiteSpace: "nowrap",
                                    minWidth: { xs: "100%", sm: "130px" },
                                }}
                            >
                                Clear
                            </MDButton>
                        )}

                        {/* Back Button */}
                        {onBack && (
                            <MDButton
                                variant="outlined"
                                onClick={onBack}
                                startIcon={<ArrowBackOutlinedIcon />}
                                sx={{
                                    whiteSpace: "nowrap",
                                    minWidth: { xs: "100%", sm: "130px" },
                                    color: "#fff !important",
                                    borderColor: "#fff !important",
                                    "&:hover": {
                                        borderColor: "#fff",
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                    },
                                }}
                            >
                                Back
                            </MDButton>
                        )}
                    </MDBox>
                </MDBox>
            </Slide>

            {/* [NEW] RBAC Restriction Popup */}
            <UpgradePopup
                open={showRBAC}
                onClose={() => setShowRBAC(false)}
                type="rbac"
                featureName="Generating Reports"
                roleRequired={requiredRoles["reports:manage"] || "Analyst"}
            />
        </>
    );
}

export default SocialPopup;
